import React, { useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'

import { useTranslation } from '../../../context/translationProvider'
import { AuthContext } from '../../../context/authProvider'
import { Button } from '../../atoms/button'
import { DrawerSectionTitle } from '../../atoms/drawerSectionTitle'
import { ValueInput } from '../../atoms/valueInput'
import { WithdrawBankAccountListNew } from '../../organisms/WithdrawBankAccountListNew'
import { StyledAlertWrapper } from '../../organisms/WithdrawBankAccountListNew/styles'
import InformationIcon from '../../../images/icon_information.svg'
import WarningIcon from '../../../images/warning.svg'
import BasicAlert from '../../molecules/basicAlert'
import useNavbarHeight from '../../../hooks/useNavbarHeight'
import { PaymentTypes, BANK_ACCOUNT_TYPES } from '../../../utils/constants'
import { Text } from '../../../atomic-components/atoms/text'
import changeAmountIcon from '../../../images/deposit/change_amount.svg'

import {
  getLivenessStatus,
  requestBankAccountWithdraw,
} from '../../../adapters/payments'
import { toast } from '../../atoms'
import { usePreDepositModals } from '../../../hooks/usePreDepositModals'
import { If } from '../../atoms/if'
import {
  StyledWithdrawForm,
  StyledAccountDetails,
  StyledAccountDetailLine,
  StyledAmountChange,
  StyledToAccount,
  StyledWithdrawAmount,
  StyledWithdrawAmountValue,
} from '../../organisms/WithdrawDrawer/style'
import { formatCurrency } from '../../../utils/currencyFormatting'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { callWithdrawalWebhook } from '../../../helpers/callWithdrawalWebhook'

const WITHDRAW_STEPS = {
  AMOUNT: 'amount',
  ACCOUNT: 'account',
  REVIEW: 'review',
}
const DEFAULT_AMOUNT = 50
const ERROR_CODES = {
  NOT_ENOUGH_BALANCE: 46,
  NOT_ALLOWED_FOR_WITHDRAW: 43,
}
const EXPERIMENT_DETAILS = {
  experiment: 'Withdrawal Flow V1',
  featureId: 'withdrawal_new_flow',
  experiment_group: 'Variation',
}

export const BankAccountWithdrawFormNew = ({
  method,
  minimumAmount,
  maximumAmount,
  bankAccounts = [],
  startLoading,
  stopLoading,
  isMobile,
  isLoading,
  setGoBackHandler,
}) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  })
  const { translate } = useTranslation()
  const withdrawAmount = watch('withdrawAmount')
  const [selectedBank, setSelectedBank] = useState()
  const { openLivenessModal } = usePreDepositModals()
  const { user } = useContext(AuthContext)
  const currencySymbol = user?.wallet?.currency?.symbol
  const [step, setStep] = useState(WITHDRAW_STEPS.AMOUNT)
  const navbarHeight = useNavbarHeight()
  const { trackEvent, EVENT_NAMES } = useEventTracking()
  const shouldSkipBankSelection = bankAccounts.length === 1 && withdrawAmount
  const hasNoWithdrawalAccount =
    bankAccounts.every(
      (account) => account.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT
    )

  let withdrawalStepsArray
  if (hasNoWithdrawalAccount) {
    withdrawalStepsArray = [WITHDRAW_STEPS.AMOUNT]
  } else if (bankAccounts.length === 1) {
    withdrawalStepsArray = [WITHDRAW_STEPS.AMOUNT, WITHDRAW_STEPS.REVIEW]
  } else {
    withdrawalStepsArray = Object.values(WITHDRAW_STEPS)
  }
  const stepIndex = withdrawalStepsArray.indexOf(step)
  const previousStep =
    withdrawalStepsArray[stepIndex - 1] || withdrawalStepsArray[stepIndex]
  const nextStep =
    withdrawalStepsArray[stepIndex + 1] || withdrawalStepsArray[stepIndex]

  useEffect(() => {
    trackEvent(
      EVENT_NAMES.GROWTHBOOK_EXPERIMENT,
      { ...EXPERIMENT_DETAILS },
      true
    )
  }, [])

  const goBack = useCallback(() => {
    setStep(previousStep)
  }, [previousStep])

  useEffect(() => {
    setGoBackHandler(stepIndex > 0 ? () => goBack : null)

    switch (step) {
      case WITHDRAW_STEPS.AMOUNT:
        trackEvent(EVENT_NAMES.WITHDRAWAL_AMOUNT_LOADED, {}, true)
        break
      case WITHDRAW_STEPS.ACCOUNT:
        trackEvent(EVENT_NAMES.WITHDRAWAL_BANK_ACCOUNT_CHOICE_LOADED, {}, true)
        break
      case WITHDRAW_STEPS.REVIEW:
        trackEvent(EVENT_NAMES.WITHDRAWAL_REVIEW_LOADED, {}, true)
        break
      default:
        break
    }
  }, [step])

  const handleAmountContinue = () => {
    if (!withdrawAmount) return
    if (hasNoWithdrawalAccount) return

    if (shouldSkipBankSelection) {
      setSelectedBank(bankAccounts[0])
    }
    setStep(nextStep)
  }

  const handleSelectBank = (bank) => {
    setSelectedBank(bank)
    if (bank.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT) return
    setStep(nextStep)
  }

  const handleChange = () => {
    setStep(WITHDRAW_STEPS.AMOUNT)
  }

  const onWithdraw = async () => {
    trackEvent(EVENT_NAMES.WITHDRAWAL_REQUESTED, {}, true)
    startLoading()

    const response = await requestBankAccountWithdraw({
      bankAccountId: selectedBank.id,
      amount: Number(withdrawAmount),
      paymentMethodDetailId: method.paymentMethodDetailId,
    })

    if (!response.ok) {
      stopLoading()

      if (response?.error?.messageCode === ERROR_CODES.NOT_ENOUGH_BALANCE) {
        return toast.error(
          translate('payments.withdraw.error.notEnoughBalance')
        )
      }

      if (response?.error?.code === ERROR_CODES.NOT_ALLOWED_FOR_WITHDRAW) {
        return toast.error(translate('payments.withdraw.error.notAllowed'))
      }

      return toast.error(response.error.message)
    }

    stopLoading()

    const {
      data: {
        id: transactionId,
        liveness: { url, onboardingId },
      },
    } = response

    trackEvent(
      EVENT_NAMES.LIVENESS_STARTED,
      { liveness_reason: 'Withdrawal' },
      true
    )
    openLivenessModal({
      bankAccount: selectedBank,
      onCancelClose: () => {},
      onSuccessSave: () => pollLivenessStatus(onboardingId, transactionId),
      openOrigin: PaymentTypes.WITHDRAW,
      customURL: url,
      showSuccessToast: false,
    })
  }

  const pollLivenessStatus = async (onboardingId, transactionId) => {
    const response = await getLivenessStatus(onboardingId)

    if (!response.ok) {
      toast.error(response.error.message)
      return
    }

    const {
      data: { status },
    } = response

    if (status === 'APPROVED') {
      toast.success(translate('payments.withdraw.withdrawalSuccessful'))
      trackEvent(
        EVENT_NAMES.LIVENESS_COMPLETED,
        { liveness_reason: 'Withdrawal' },
        true
      )
      trackEvent(
        EVENT_NAMES.WITHDRAWAL_REQUEST_SUCCESSFUL,
        { ...EXPERIMENT_DETAILS, transaction_id: transactionId },
        true
      )
      callWithdrawalWebhook(transactionId, 'Variation')
      return
    }

    if (status === 'REPROVED') {
      toast.error(translate('payments.receipt.error'))
      return
    }

    setTimeout(() => pollLivenessStatus(onboardingId, transactionId), 500)
  }

  const validateAmount = (value) => {
    if (!value) return true

    const isBelowMinimum = value < minimumAmount && value !== 0
    const isAboveBalance = value > user?.wallet?.amount
    const isAboveMaximum = value > maximumAmount

    if (isBelowMinimum) {
      return `${translate(
        'payments.minimumWithdrawalAmount'
      )} ${currencySymbol}${minimumAmount}`
    }

    if (isAboveBalance) {
      return translate('payments.withdraw.notEnoughBalance')
    }

    if (isAboveMaximum) {
      return `${translate(
        'payments.maximumWithdrawalAmount'
      )} ${currencySymbol}${maximumAmount}`
    }

    return true
  }

  const isValidAmount =
    withdrawAmount > 0 &&
    withdrawAmount > minimumAmount &&
    withdrawAmount <= user?.wallet?.amount &&
    withdrawAmount <= maximumAmount

  const isAmountContinueDisabled =
    !isValidAmount || isLoading || hasNoWithdrawalAccount

  const isSubmitDisabled =
    !isValidAmount ||
    isLoading ||
    selectedBank?.accountType === BANK_ACCOUNT_TYPES.SALARY_ACCOUNT

  return (
    <>
      <StyledWithdrawForm
        onSubmit={handleSubmit(onWithdraw)}
        isMobile={isMobile}
        navbarHeight={navbarHeight}
        expand
      >
        <If
          condition={step === WITHDRAW_STEPS.AMOUNT}
          render={() => (
            <>
              <section>
                <DrawerSectionTitle
                  title={translate('payments.withdraw.howMuch')}
                />

                <p>
                  {translate('payments.withdraw.availableBalance')?.replace(
                    '{0}',
                    `${currencySymbol}${user?.wallet?.amount}`
                  )}
                </p>
                <Controller
                  name="withdrawAmount"
                  control={control}
                  rules={{
                    required: true,
                    validate: validateAmount,
                  }}
                  render={({ field }) => (
                    <ValueInput
                      {...field}
                      forceValue={withdrawAmount}
                      placeholder={DEFAULT_AMOUNT}
                      currencySymbol={currencySymbol}
                      errors={errors?.withdrawAmount}
                      disabled={false}
                      inputMode="numeric"
                    />
                  )}
                />

                <If
                  condition={hasNoWithdrawalAccount}
                  render={() => (
                    <StyledAlertWrapper>
                      <BasicAlert
                        mobileIcon={WarningIcon}
                        desktopIcon={InformationIcon}
                        description={translate(
                          'addBankAccount.salaryAccountWarning'
                        )}
                      />
                    </StyledAlertWrapper>
                  )}
                />
              </section>

              <Button
                expand
                disabled={isAmountContinueDisabled}
                id="btnWithdrawalContinue"
                isLoading={isLoading}
                className="btn-cta"
                onClick={handleAmountContinue}
              >
                {translate('common.continue')}
              </Button>
            </>
          )}
        />

        <If
          condition={step === WITHDRAW_STEPS.ACCOUNT}
          render={() => (
            <WithdrawBankAccountListNew
              selectedBank={selectedBank}
              onSelectBank={handleSelectBank}
              bankAccounts={bankAccounts}
            />
          )}
        />

        <If
          condition={step === WITHDRAW_STEPS.REVIEW}
          render={() => (
            <>
              <section>
                <DrawerSectionTitle
                  title={translate('payments.withdraw.withdrawing')}
                />
                <StyledWithdrawAmount>
                  <StyledWithdrawAmountValue>
                    {currencySymbol} {formatCurrency(withdrawAmount)}
                  </StyledWithdrawAmountValue>
                  <If
                    condition={isMobile}
                    render={() => (
                      <StyledAmountChange onClick={handleChange}>
                        <img src={changeAmountIcon} alt="Change amount Icon" />
                        <span>
                          {translate('payments.deposit.changeButton')}
                        </span>
                      </StyledAmountChange>
                    )}
                  />
                </StyledWithdrawAmount>

                <StyledToAccount>
                  {translate('payments.withdraw.to')}{' '}
                  <Text bold>{selectedBank?.bank?.name}</Text>
                </StyledToAccount>

                <If
                  condition={!isMobile}
                  render={() => (
                    <StyledAmountChange onClick={handleChange}>
                      <img src={changeAmountIcon} alt="Change amount Icon" />
                      <span>{translate('payments.deposit.changeButton')}</span>
                    </StyledAmountChange>
                  )}
                />

                <StyledAccountDetails>
                  <StyledAccountDetailLine>
                    <span>{translate('payments.withdraw.branch')}:</span>
                    <span>{selectedBank?.bankBranch}</span>
                  </StyledAccountDetailLine>
                  <StyledAccountDetailLine>
                    <span>
                      {translate('payments.withdraw.checkingAccount')}:
                    </span>
                    <span>{selectedBank?.accountNumber}</span>
                  </StyledAccountDetailLine>
                  <StyledAccountDetailLine>
                    <span>{translate('payments.withdraw.date')}:</span>
                    <span>{new Date().toLocaleDateString('pt-BR')}</span>
                  </StyledAccountDetailLine>
                  <StyledAccountDetailLine>
                    <span>
                      {translate('payments.withdraw.balanceBeforeWithdrawal')}:
                    </span>
                    <span>
                      {currencySymbol} {user?.wallet?.amount}
                    </span>
                  </StyledAccountDetailLine>
                </StyledAccountDetails>
              </section>

              <Button
                expand
                disabled={isSubmitDisabled}
                id="btnWithdrawalModal"
                className="btn-cta"
                isLoading={isLoading}
              >
                {translate('payments.withdraw.withdraw')}
              </Button>
            </>
          )}
        />
      </StyledWithdrawForm>
    </>
  )
}

BankAccountWithdrawFormNew.propTypes = {
  method: PropTypes.object,
  minimumAmount: PropTypes.number,
  maximumAmount: PropTypes.number,
  bankAccounts: PropTypes.array,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  setGoBackHandler: PropTypes.func,
}
